/**
 * DispatchNotesModal — read-only view of every dispatcher instruction on a
 * scheduled visit (iter269).
 *
 * The dispatcher writes materials to bring, site contacts and special
 * instructions when scheduling. The tech card only had room for the most
 * recent few, so this panel gives them the full, timestamped history in
 * one tap from the schedule.
 */
import { X, StickyNote } from "lucide-react";
import { useI18n } from "@/i18n";
import { Tx } from "@/i18n/Tx";
import { NoteText } from "@/i18n/NoteText";

function fmt(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export default function DispatchNotesModal({ title, subtitle, notes = [], fieldNotes = [], onClose }) {
  const { t } = useI18n();
  const dispatchNotes = Array.isArray(notes) ? notes : [];
  const techNotes = Array.isArray(fieldNotes) ? fieldNotes : [];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose} data-testid="dispatch-notes-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[85vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3 sticky top-0 bg-white">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
              <StickyNote className="w-4 h-4 text-amber-600" />{t("Dispatch Notes")}
            </div>
            <div className="text-xs text-slate-500 truncate">{title}{subtitle ? ` · ${subtitle}` : ""}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="btn-close-dispatch-notes">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="space-y-2">
            {dispatchNotes.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-6" data-testid="dispatch-notes-empty">
                {t("No dispatch notes on this visit yet.")}
              </div>
            ) : dispatchNotes.map((n, i) => (
              <div key={n.id || i} className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                   data-testid={`dispatch-notes-modal-row-${i}`}>
                <div className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide">
                  {n.created_by || "Dispatch"}{n.created_at ? ` · ${fmt(n.created_at)}` : ""}
                </div>
                <div className="text-amber-900 whitespace-pre-wrap break-words mt-0.5"><NoteText note={n} /></div>
              </div>
            ))}
          </div>

          {techNotes.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{t("Field notes from previous visits")}</div>
              {techNotes.map((n, i) => (
                <div key={n.id || i} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                     data-testid={`dispatch-notes-modal-field-${i}`}>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                    {n.tech_name || n.created_by || "Tech"}{n.created_at ? ` · ${fmt(n.created_at)}` : ""}
                  </div>
                  <div className="text-slate-700 whitespace-pre-wrap break-words mt-0.5">
                    {typeof n === "string" ? <Tx text={n} /> : <NoteText note={n} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
